
















import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import useAuthUser from "@/hooks/useAuthUser";
import config from "@/config";
import { registerWatermarkWatchdog, unregisterWatermarkWatchdog } from "./watermarkWatchdog";

const MARKS = [
  { position: "top-left", opacity: "0.05" },
  { position: "top-right", opacity: "0.05" },
  { position: "bottom-left", opacity: "0.05" },
  { position: "bottom-right", opacity: "0.05" },
  { position: "center", opacity: "0.1" },
] as const;

const OVERLAY_CLASS = "user-watermark-overlay";
const MARK_CLASS = "user-watermark-overlay__mark";

function isEffectivelyHidden(el: HTMLElement) {
  if (el.hidden || el.hasAttribute("hidden")) return true;
  const style = window.getComputedStyle(el);
  return style.display === "none" || style.visibility === "hidden" || style.opacity === "0";
}

function restoreVisibility(el: HTMLElement, expectedOpacity?: string) {
  if (el.hidden) el.hidden = false;
  if (el.hasAttribute("hidden")) el.removeAttribute("hidden");
  if (!isEffectivelyHidden(el)) return true;
  if (el.style.display === "none") el.style.removeProperty("display");
  if (el.style.visibility === "hidden") el.style.removeProperty("visibility");
  if (el.style.opacity === "0") {
    if (expectedOpacity) el.style.opacity = expectedOpacity;
    else el.style.removeProperty("opacity");
  }
  if (!isEffectivelyHidden(el)) return true;
  el.style.setProperty("display", "block", "important");
  el.style.setProperty("visibility", "visible", "important");
  el.style.setProperty("opacity", expectedOpacity || "1", "important");
  return !isEffectivelyHidden(el);
}

function restoreOverlayClasses(el: HTMLElement) {
  [...el.classList].forEach(name => {
    if (name !== OVERLAY_CLASS) el.classList.remove(name);
  });
  if (!el.classList.contains(OVERLAY_CLASS)) el.classList.add(OVERLAY_CLASS);
}

function restoreMarkClasses(el: HTMLElement) {
  const position = el.dataset.position || "";
  [...el.classList].forEach(name => {
    if (name !== MARK_CLASS && name !== `__${position}`) el.classList.remove(name);
  });
  if (!el.classList.contains(MARK_CLASS)) el.classList.add(MARK_CLASS);
  if (position && !el.classList.contains(`__${position}`)) el.classList.add(`__${position}`);
}

export default defineComponent({
  name: "UserWatermarkOverlay",
  setup(_, { emit }) {
    const root = ref<HTMLElement>();
    const generation = ref(0);
    const { authUser } = useAuthUser();
    const marks = MARKS;

    const visible = computed(() => Boolean(config.featureUserWatermark && authUser.value));
    const text = computed(() => {
      if (!authUser.value) return "";
      return `@${authUser.value.username} ${authUser.value.id}`;
    });

    let host: HTMLElement | null = null;
    let unmounted = false;
    let restoring = false;
    let rebuildingMarks = false;
    let tamperEmitted = false;
    let ensureOverlay: () => boolean = () => false;

    const emitTamper = () => {
      if (tamperEmitted || unmounted) return;
      tamperEmitted = true;
      emit("tamper");
    };

    const bindHost = (el: HTMLElement) => {
      host = el.parentElement;
      return Boolean(host && document.contains(host));
    };

    const onMutate = () => {
      if (restoring || unmounted || !visible.value) return;
      restoring = true;
      try {
        if (!ensureOverlay()) emitTamper();
      } finally {
        restoring = false;
      }
    };

    ensureOverlay = () => {
      const el = root.value;
      if (!el || !host) return false;
      if (!document.contains(host)) return false;
      if (!host.contains(el)) {
        host.appendChild(el);
      }
      restoreOverlayClasses(el);
      if (!restoreVisibility(el)) {
        emitTamper();
        return true;
      }

      const markNodes = [...el.querySelectorAll<HTMLElement>(`.${MARK_CLASS}`)];
      if (markNodes.length !== MARKS.length) {
        if (!rebuildingMarks) {
          rebuildingMarks = true;
          generation.value += 1;
          nextTick(() => {
            rebuildingMarks = false;
            if (unmounted) return;
            const nextEl = root.value;
            if (!nextEl || !bindHost(nextEl)) {
              emitTamper();
              return;
            }
            if (nextEl.querySelectorAll(`.${MARK_CLASS}`).length !== MARKS.length) {
              emitTamper();
              return;
            }
            onMutate();
          });
        }
        return true;
      }

      const marksVisible = markNodes.every(mark => {
        restoreMarkClasses(mark);
        return restoreVisibility(mark, mark.dataset.opacity);
      });
      if (!marksVisible) emitTamper();
      return true;
    };

    const stopWatchdog = () => {
      unregisterWatermarkWatchdog(onMutate);
      host = null;
    };

    const startWatchdog = () => {
      stopWatchdog();
      const el = root.value;
      if (!el?.parentElement) return;
      host = el.parentElement;
      tamperEmitted = false;
      registerWatermarkWatchdog(onMutate);
    };

    onMounted(() => {
      if (visible.value) startWatchdog();
    });

    watch(visible, async isVisible => {
      if (isVisible) {
        await nextTick();
        if (!unmounted) startWatchdog();
      } else {
        stopWatchdog();
      }
    });

    onBeforeUnmount(() => {
      unmounted = true;
      stopWatchdog();
    });

    return {
      root,
      visible,
      text,
      marks,
      generation,
    };
  },
});
